import React, { useState, useEffect } from 'react';
import { Card, Button, Tag, Input, Select, message, Modal, Typography, Divider, Table } from 'antd';
import { 
  PlusOutlined, 
  PlayCircleOutlined, 
  StopOutlined, 
  ReloadOutlined,
  EyeOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

const { Search } = Input;
const { Option } = Select;
const { confirm } = Modal;
const { Text, Title } = Typography;

interface Server {
  id: number;
  name: string;
  game_name: string;
  status: string;
  port: number;
  expires_at: string;
  node_name: string;
  max_players: number;
}

const Servers: React.FC = () => {
  const [servers, setServers] = useState<Server[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<{ [key: number]: string }>({});
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const navigate = useNavigate();

  useEffect(() => {
    loadServers();
  }, []);

  const loadServers = async () => {
    try {
      const response = await api.get('/users/servers');
      setServers(response.data);
    } catch (error) {
      console.error('加载服务器失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleServerAction = async (serverId: number, action: string) => {
    setActionLoading(prev => ({ ...prev, [serverId]: action }));
    
    try {
      if (action === 'start') {
        await api.post(`/server-management/${serverId}/start`);
      } else if (action === 'stop') {
        await api.post(`/server-management/${serverId}/stop`);
      } else if (action === 'restart') {
        await api.post(`/server-management/${serverId}/restart`);
      } else {
        await api.post(`/servers/${serverId}/${action}`);
      }
      message.success(`服务器${action === 'start' ? '启动' : action === 'stop' ? '停止' : '重启'}指令已发送`);
      
      // 更新服务器状态
      setServers(prev => prev.map(server => 
        server.id === serverId 
          ? { ...server, status: action === 'start' ? 'starting' : action === 'stop' ? 'stopped' : 'restarting' }
          : server
      ));
    } catch (error: any) {
      message.error(error.response?.data?.message || '操作失败');
    } finally {
      setActionLoading(prev => ({ ...prev, [serverId]: '' }));
    }
  };

  const handleDeleteServer = (server: Server) => {
    confirm({
      title: '确认删除服务器',
      icon: <ExclamationCircleOutlined />,
      content: `确定要删除服务器 "${server.name}" 吗？此操作不可撤销！`,
      okText: '确认删除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          await api.delete(`/servers/${server.id}`);
          message.success('服务器删除成功');
          loadServers();
        } catch (error: any) {
          message.error(error.response?.data?.message || '删除失败');
        }
      },
    });
  };

  const getStatusTag = (status: string) => {
    const statusMap = {
      running: { color: 'green', text: '运行中' },
      stopped: { color: 'red', text: '已停止' },
      starting: { color: 'orange', text: '启动中' },
      restarting: { color: 'orange', text: '重启中' },
      installing: { color: 'blue', text: '安装中' },
      error: { color: 'red', text: '错误' }
    };
    
    const statusInfo = statusMap[status as keyof typeof statusMap] || { color: 'default', text: status };
    return (
      <Tag 
        color={statusInfo.color}
        style={{
          fontSize: '12px',
          fontWeight: 'bold',
          padding: '4px 4px',
          borderRadius: '6px',
          minWidth: '64px',
          minHeight: '32px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        {statusInfo.text}
      </Tag>
    );
  };

  const filteredServers = servers.filter(server => {
    const matchSearch = server.name.toLowerCase().includes(search.toLowerCase()) ||
                       server.game_name.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'all' || server.status === statusFilter;
    return matchSearch && matchStatus;
  });

  // 桌面端多列表格渲染
  const renderDesktopTable = () => {
    const columns = [
      {
        title: '服务器信息',
        key: 'server_info',
        width: '25%',
        render: (server: Server) => (
          <div>
            <div style={{ 
              fontSize: '16px', 
              fontWeight: 'bold', 
              marginBottom: '4px',
              color: 'var(--text-color)',
              wordBreak: 'break-word'
            }}>
              {server.name}
            </div>
            <div style={{ 
              fontSize: '12px', 
              color: 'var(--text-secondary-color)',
              marginBottom: '6px'
            }}>
              {server.game_name}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {getStatusTag(server.status)}
            </div>
          </div>
        ),
      },
      {
        title: '端口',
        key: 'port',
        width: '8%',
        render: (server: Server) => (
          <div style={{ 
            fontSize: '14px', 
            fontWeight: 'bold',
            color: 'var(--text-color)'
          }}>
            {server.port}
          </div>
        ),
      },
      {
        title: '最大玩家',
        key: 'max_players',
        width: '8%',
        render: (server: Server) => (
          <div style={{ 
            fontSize: '14px', 
            fontWeight: 'bold',
            color: 'var(--text-color)'
          }}>
            {server.max_players}
          </div>
        ),
      },
      {
        title: '节点',
        key: 'node',
        width: '20%',
        render: (server: Server) => (
          <div style={{ 
            fontSize: '14px', 
            color: 'var(--text-color)',
            wordBreak: 'break-word'
          }}>
            {server.node_name}
          </div>
        ),
      },
      {
        title: '到期时间',
        key: 'expires_at',
        width: '15%',
        render: (server: Server) => {
          const expireDate = new Date(server.expires_at);
          const now = new Date();
          const isExpired = expireDate < now;
          const timeLeft = expireDate.getTime() - now.getTime();
          const daysLeft = Math.ceil(timeLeft / (1000 * 60 * 60 * 24));
          
          return (
            <div>
              <div style={{ 
                fontSize: '12px',
                color: isExpired ? 'var(--error-color)' : 'var(--text-color)',
                fontWeight: 'bold'
              }}>
                {expireDate.toLocaleDateString()}
              </div>
              <div style={{ 
                fontSize: '11px',
                color: isExpired ? 'var(--error-color)' : 'var(--text-secondary-color)'
              }}>
                {isExpired ? '已过期' : `剩余 ${daysLeft} 天`}
              </div>
            </div>
          );
        },
      },
      {
        title: '操作',
        key: 'actions',
        width: '24%',
        render: (server: Server) => {
          const currentAction = actionLoading[server.id];
          const expireDate = new Date(server.expires_at);
          const now = new Date();
          const isExpired = expireDate < now;
          
          return (
            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
              <Button
                danger
                size="small"
                icon={<DeleteOutlined />}
                onClick={() => handleDeleteServer(server)}
              >
                删除
              </Button>
              
              {server.status === 'stopped' && !isExpired && (
                <Button
                  type="primary"
                  size="small"
                  icon={<PlayCircleOutlined />}
                  loading={currentAction === 'start'}
                  onClick={() => handleServerAction(server.id, 'start')}
                  style={{ 
                    backgroundColor: '#52c41a',
                    borderColor: '#52c41a'
                  }}
                >
                  启动
                </Button>
              )}
              
              {server.status === 'running' && (
                <>
                  <Button
                    size="small"
                    icon={<StopOutlined />}
                    loading={currentAction === 'stop'}
                    onClick={() => handleServerAction(server.id, 'stop')}
                  >
                    停止
                  </Button>
                  <Button
                    size="small"
                    icon={<ReloadOutlined />}
                    loading={currentAction === 'restart'}
                    onClick={() => handleServerAction(server.id, 'restart')}
                  >
                    重启
                  </Button>
                </>
              )}
              
              <Button
                type="primary"
                size="small"
                icon={<EyeOutlined />}
                onClick={() => navigate(`/servers/${server.id}`)}
              >
                管理
              </Button>
            </div>
          );
        },
      },
    ];

    return (
      <Table
        columns={columns}
        dataSource={filteredServers}
        rowKey="id"
        pagination={false}
        scroll={{ x: 'max-content' }}
        size="middle"
        className="server-list-table"
      />
    );
  };

  // 移动端卡片布局渲染
  const renderMobileCard = (server: Server) => {
    const currentAction = actionLoading[server.id];
    const expireDate = new Date(server.expires_at);
    const now = new Date();
    const isExpired = expireDate < now;
    const timeLeft = expireDate.getTime() - now.getTime();
    const daysLeft = Math.ceil(timeLeft / (1000 * 60 * 60 * 24));

    return (
      <Card 
        key={server.id} 
        size="small" 
        className="server-list-card"
        style={{ 
          marginBottom: '24px',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          border: '1px solid #e8e8e8',
          padding: '20px'
        }}
        title={
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column',
            gap: '0px'
          }}>
            {/* 顶部标题框架 */}
            <div style={{
              textAlign: 'center',
              padding: '8px 12px',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              border: '1px solid #e9ecef',
              marginBottom: '12px'
            }}>
              <Title level={4} style={{
                margin: 0,
                fontSize: '18px',
                fontWeight: 'bold',
                color: 'var(--text-color)',
                lineHeight: '1.3',
                wordBreak: 'break-word'
              }}>
                {server.name}
              </Title>
              <div style={{
                fontSize: '14px',
                color: 'var(--text-secondary-color)',
                fontWeight: '500',
                marginTop: '4px'
              }}>
                {server.game_name}
              </div>
            </div>

            {/* 状态和操作按钮行 */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '8px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                {getStatusTag(server.status)}
              </div>
              <Button
                danger
                size="small"
                icon={<DeleteOutlined />}
                onClick={() => handleDeleteServer(server)}
                style={{
                  minWidth: 'auto',
                  padding: '2px 6px',
                  height: '24px',
                  fontSize: '10px',
                  fontWeight: 'bold',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                删除
              </Button>
            </div>
          </div>
        }
      >
        <div style={{ padding: '8px 0' }}>
          {/* 服务器信息网格 */}
          <div style={{ 
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '12px',
            marginBottom: '12px'
          }}>
            {/* 端口信息 */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '8px 12px',
              backgroundColor: '#f8f9fa',
              borderRadius: '6px',
              border: '1px solid #e9ecef'
            }}>
              <Text strong style={{ 
                fontSize: '12px', 
                color: 'var(--text-secondary-color)',
                fontWeight: '600'
              }}>
                端口:
              </Text>
              <Text style={{ 
                fontSize: '14px',
                fontWeight: 'bold',
                color: 'var(--text-color)'
              }}>
                {server.port}
              </Text>
            </div>
            
            {/* 最大玩家信息 */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '8px 12px',
              backgroundColor: '#f8f9fa',
              borderRadius: '6px',
              border: '1px solid #e9ecef'
            }}>
              <Text strong style={{ 
                fontSize: '12px', 
                color: 'var(--text-secondary-color)',
                fontWeight: '600'
              }}>
                玩家:
              </Text>
              <Text style={{ 
                fontSize: '14px',
                fontWeight: 'bold',
                color: 'var(--text-color)'
              }}>
                {server.max_players}
              </Text>
            </div>
          </div>
          
          {/* 节点信息 */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '8px 12px',
            backgroundColor: '#f8f9fa',
            borderRadius: '6px',
            border: '1px solid #e9ecef',
            marginBottom: '12px'
          }}>
            <Text strong style={{ 
              fontSize: '12px', 
              color: 'var(--text-secondary-color)',
              fontWeight: '600'
            }}>
              节点:
            </Text>
            <Text style={{ 
              fontSize: '14px',
              color: 'var(--text-color)',
              fontWeight: 'bold',
              wordBreak: 'break-word',
              textAlign: 'right',
              maxWidth: '60%'
            }}>
              {server.node_name}
            </Text>
          </div>
          
          {/* 到期时间信息 */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '8px 12px',
            backgroundColor: isExpired ? '#fff2f0' : '#f6ffed',
            borderRadius: '6px',
            border: `1px solid ${isExpired ? '#ffccc7' : '#b7eb8f'}`
          }}>
            <Text strong style={{ 
              fontSize: '12px', 
              color: isExpired ? '#ff4d4f' : '#52c41a',
              fontWeight: '600'
            }}>
              到期:
            </Text>
            <Text style={{ 
              fontSize: '14px',
              fontWeight: 'bold',
              color: isExpired ? '#ff4d4f' : '#52c41a'
            }}>
              {isExpired ? '已过期' : `${expireDate.toLocaleDateString()} (剩余${daysLeft}天)`}
            </Text>
          </div>
        </div>
        
        <Divider style={{ margin: '12px 0 8px 0' }} />
        
        {/* 服务器控制按钮区域 */}
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column',
          gap: '8px'
        }}>
          {/* 状态控制按钮 */}
          <div style={{ 
            display: 'flex', 
            gap: '8px',
            justifyContent: 'center'
          }}>
            {server.status === 'stopped' && !isExpired && (
              <Button
                type="primary"
                size="middle"
                icon={<PlayCircleOutlined />}
                loading={currentAction === 'start'}
                onClick={() => handleServerAction(server.id, 'start')}
                style={{
                  backgroundColor: '#52c41a',
                  borderColor: '#52c41a',
                  height: '40px',
                  minWidth: '120px',
                  fontWeight: 'bold',
                  fontSize: '14px',
                  borderRadius: '8px',
                  boxShadow: '0 2px 4px rgba(82, 196, 26, 0.3)',
                  transition: 'all 0.3s ease'
                }}
              >
                启动服务器
              </Button>
            )}
            
            {server.status === 'running' && (
              <>
                <Button
                  type="primary"
                  danger
                  size="middle"
                  icon={<StopOutlined />}
                  loading={currentAction === 'stop'}
                  onClick={() => handleServerAction(server.id, 'stop')}
                  style={{
                    height: '40px',
                    minWidth: '80px',
                    fontWeight: 'bold',
                    fontSize: '14px',
                    borderRadius: '8px',
                    boxShadow: '0 2px 4px rgba(255, 77, 79, 0.3)',
                    transition: 'all 0.3s ease'
                  }}
                >
                  停止
                </Button>
                <Button
                  type="primary"
                  size="small"
                  icon={<ReloadOutlined />}
                  loading={currentAction === 'restart'}
                  onClick={() => handleServerAction(server.id, 'restart')}
                  style={{
                    backgroundColor: '#1890ff',
                    borderColor: '#1890ff',
                    height: '32px',
                    minWidth: '60px',
                    fontWeight: 'bold',
                    fontSize: '12px',
                    borderRadius: '6px',
                    boxShadow: '0 2px 4px rgba(24, 144, 255, 0.3)',
                    transition: 'all 0.3s ease'
                  }}
                >
                  重启
                </Button>
              </>
            )}
          </div>

          {/* 管理按钮 */}
          <div style={{ 
            textAlign: 'center',
            paddingTop: '6px',
            borderTop: '1px solid #f0f0f0'
          }}>
            <Button
              type="primary"
              size="middle"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/servers/${server.id}`)}
              style={{
                height: '36px',
                minWidth: '100px',
                fontWeight: 'bold',
                fontSize: '14px',
                backgroundColor: '#722ed1',
                borderColor: '#722ed1',
                borderRadius: '8px',
                boxShadow: '0 4px 8px rgba(114, 46, 209, 0.3)',
                transition: 'all 0.3s ease'
              }}
            >
              管理服务器
            </Button>
          </div>
        </div>
      </Card>
    );
  };

  return (
    <div className="fade-in">
      <Card style={{ padding: '20px' }}>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">我的服务器</h2>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/servers/create')}
          >
            创建服务器
          </Button>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <Search
            placeholder="搜索服务器名称或游戏类型"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1"
          />
          <Select
            value={statusFilter}
            onChange={setStatusFilter}
            className="w-32"
          >
            <Option value="all">全部状态</Option>
            <Option value="running">运行中</Option>
            <Option value="stopped">已停止</Option>
            <Option value="starting">启动中</Option>
            <Option value="installing">安装中</Option>
            <Option value="error">错误</Option>
          </Select>
        </div>

        {/* 桌面端表格布局 */}
        <div className="desktop-table-layout">
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <div>加载中...</div>
            </div>
          ) : (
            <div>
              {renderDesktopTable()}
              <div style={{ 
                textAlign: 'center', 
                marginTop: '16px',
                color: 'var(--text-secondary-color)'
              }}>
                共 {filteredServers.length} 个服务器
              </div>
            </div>
          )}
        </div>

        {/* 移动端卡片布局 */}
        <div style={{ display: 'block' }} className="mobile-card-layout">
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <div>加载中...</div>
            </div>
          ) : (
            <div>
              {filteredServers.map(renderMobileCard)}
              <div style={{ 
                textAlign: 'center', 
                marginTop: '20px',
                color: 'var(--text-secondary-color)',
                fontSize: '14px',
                fontWeight: '500'
              }}>
                共 {filteredServers.length} 个服务器
              </div>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default Servers;



